import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import { Button, Card, Pill, SectionLabel } from "./ui";
import { Ic } from "./icons";
import {
  ACCENT_PRESETS,
  DEFAULT_BRANDING,
  TYPOGRAPHY_PAIRINGS,
  brandingReadiness,
  contrastRatio,
  deleteImage,
  extractDominantColor,
  normalizeHex,
  readableForeground,
  saveBranding,
  subscribeBranding,
  uploadImage,
  type Agency,
  type AgentSignature,
  type Branding,
  type Palette,
  type Typography,
  type TypographyPairing,
} from "../lib/branding";
import { useAuth } from "../context/AuthContext";

type SaveStatus = "idle" | "saving" | "saved" | "error";

export function BrandingTab() {
  const { user } = useAuth();
  const [branding, setBranding] = useState<Branding>(DEFAULT_BRANDING);
  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState<SaveStatus>("idle");
  const skipSaveRef = useRef(true); // skip autosave on first hydration
  const saveTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!user) return;
    const unsub = subscribeBranding((b) => {
      // Only take the remote snapshot as source of truth on first load. After
      // that, local state is authoritative (the user is editing) — live-
      // syncing would fight with their typing.
      if (!ready) {
        skipSaveRef.current = true;
        setBranding(b);
        setReady(true);
      }
    });
    return unsub;
  }, [user, ready]);

  // Debounced autosave — 800 ms after the last change.
  useEffect(() => {
    if (!user || !ready) return;
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }
    if (saveTimer.current != null) window.clearTimeout(saveTimer.current);
    setStatus("saving");
    saveTimer.current = window.setTimeout(async () => {
      try {
        await saveBranding(branding);
        setStatus("saved");
        window.setTimeout(() => setStatus("idle"), 1200);
      } catch (e) {
        console.error("[branding] save failed", e);
        setStatus("error");
      }
    }, 800);
    return () => {
      if (saveTimer.current != null) window.clearTimeout(saveTimer.current);
    };
  }, [branding, user, ready]);

  // Partial updaters — keep callsites terse.
  const updateAgency = useCallback((patch: Partial<Agency>) => {
    setBranding((b) => ({ ...b, agency: { ...b.agency, ...patch } }));
  }, []);
  const updatePalette = useCallback((patch: Partial<Palette>) => {
    setBranding((b) => ({ ...b, palette: { ...b.palette, ...patch } }));
  }, []);
  const updateTypography = useCallback((patch: Partial<Typography>) => {
    setBranding((b) => ({ ...b, typography: { ...b.typography, ...patch } }));
  }, []);
  const updateAgent = useCallback((patch: Partial<AgentSignature>) => {
    setBranding((b) => ({ ...b, agent: { ...b.agent, ...patch } }));
  }, []);

  const readiness = useMemo(() => brandingReadiness(branding), [branding]);

  if (!user) {
    return (
      <Card className="p-10 text-center">
        <div className="w-14 h-14 rounded-full bg-ink-50 hairline grid place-items-center mx-auto text-ink-500">
          <Ic.Palette size={18} />
        </div>
        <h2 className="mt-4 text-[18px] font-semibold tracking-tight">
          Connexion requise
        </h2>
        <p className="mt-2 text-[13px] text-ink-600">
          Connectez-vous pour personnaliser votre branding.
        </p>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-6 items-start">
      <div className="space-y-6 min-w-0">
        <SaveIndicator status={status} readiness={readiness} />

        <LogoSection
          agency={branding.agency}
          onLogoChange={(url, path) => updateAgency({ logoUrl: url, logoPath: path })}
          onSuggestAccent={(hex) => updatePalette({ accentHex: hex })}
        />

        <IdentitySection agency={branding.agency} onChange={updateAgency} />

        <PaletteSection
          palette={branding.palette}
          onChange={updatePalette}
        />

        <TypographySection
          typography={branding.typography}
          onChange={updateTypography}
        />

        <AgentSection
          agent={branding.agent}
          userEmail={user.email ?? null}
          onChange={updateAgent}
        />

        <LegalSection agency={branding.agency} onChange={updateAgency} />
      </div>

      <div className="xl:sticky xl:top-[88px]">
        <PreviewPanel branding={branding} />
      </div>
    </div>
  );
}

/* ═══════════════ Sections ═══════════════ */

function LogoSection({
  agency,
  onLogoChange,
  onSuggestAccent,
}: {
  agency: Agency;
  onLogoChange: (url: string | null, path: string | null) => void;
  onSuggestAccent: (hex: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [suggestedAccent, setSuggestedAccent] = useState<string | null>(null);

  async function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setSuggestedAccent(null);
    try {
      const [upload, extracted] = await Promise.all([
        uploadImage("logo", file),
        extractDominantColor(file),
      ]);
      const prevPath = agency.logoPath;
      onLogoChange(upload.url, upload.path);
      if (prevPath && prevPath !== upload.path) deleteImage(prevPath);
      if (extracted) setSuggestedAccent(extracted);
    } catch (e) {
      console.error("[logo] upload failed", e);
      alert("Upload du logo impossible. Réessayez.");
    } finally {
      setUploading(false);
    }
  }

  async function onRemove() {
    const prevPath = agency.logoPath;
    onLogoChange(null, null);
    if (prevPath) deleteImage(prevPath);
  }

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <SectionLabel>Logo</SectionLabel>
          <p className="mt-1.5 text-[13px] text-ink-600 max-w-[360px]">
            PNG ou SVG, fond transparent idéal. Affiché sur toutes les fiches
            et la page de garde.
          </p>
        </div>
        <div className="w-[120px] h-[120px] rounded-xl2 bg-ink-50 hairline grid place-items-center overflow-hidden shrink-0">
          {agency.logoUrl ? (
            <img
              src={agency.logoUrl}
              alt="Logo agence"
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <Ic.Palette size={22} className="text-ink-400" />
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <label className="cursor-pointer">
          <input
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            className="hidden"
            onChange={onPick}
            disabled={uploading}
          />
          <span className="inline-flex items-center justify-center gap-2 rounded-full font-medium transition h-8 px-3 text-[12.5px] bg-paper text-ink-900 hairline hover:bg-ink-50">
            <Ic.Plus size={13} />
            <span>{agency.logoUrl ? "Remplacer" : "Téléverser"}</span>
          </span>
        </label>
        {agency.logoUrl && (
          <Button variant="ghost" size="sm" onClick={onRemove}>
            Supprimer
          </Button>
        )}
        {uploading && <span className="text-[12px] text-ink-500">Envoi…</span>}
      </div>

      {suggestedAccent && (
        <div className="mt-4 p-3 rounded-xl2 bg-ink-50 hairline flex items-center gap-3">
          <div
            className="w-7 h-7 rounded-full shrink-0 hairline"
            style={{ background: suggestedAccent }}
          />
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] text-ink-900 font-medium">
              Couleur dominante détectée
            </div>
            <div className="text-[11.5px] text-ink-500 font-mono">
              {suggestedAccent}
            </div>
          </div>
          <Button
            variant="soft"
            size="sm"
            onClick={() => {
              onSuggestAccent(suggestedAccent);
              setSuggestedAccent(null);
            }}
          >
            Utiliser
          </Button>
        </div>
      )}
    </Card>
  );
}

function IdentitySection({
  agency,
  onChange,
}: {
  agency: Agency;
  onChange: (patch: Partial<Agency>) => void;
}) {
  return (
    <Card className="p-6">
      <SectionLabel>Identité de l'agence</SectionLabel>
      <div className="mt-4 grid gap-4">
        <Field
          label="Nom de l'agence"
          value={agency.name ?? ""}
          onChange={(v) => onChange({ name: v || null })}
          placeholder="Agence Hexagone"
        />
        <Field
          label="Baseline"
          value={agency.baseline ?? ""}
          onChange={(v) => onChange({ baseline: v || null })}
          placeholder="Votre conseiller immobilier à Boulogne"
          hint="Une phrase courte, affichée sous le logo."
        />
      </div>
    </Card>
  );
}

function PaletteSection({
  palette,
  onChange,
}: {
  palette: Palette;
  onChange: (patch: Partial<Palette>) => void;
}) {
  const [hexInput, setHexInput] = useState(palette.accentHex);
  useEffect(() => {
    setHexInput(palette.accentHex);
  }, [palette.accentHex]);

  const contrastVsWhite = contrastRatio(palette.accentHex, "#FFFFFF");
  const contrastVsInk = contrastRatio(palette.accentHex, "#14161A");
  const contrastWarning =
    contrastVsWhite < 3 && contrastVsInk < 3
      ? "Contraste faible — illisible sur la plupart des fonds."
      : null;

  function commitHex(v: string) {
    const n = normalizeHex(v);
    if (n) onChange({ accentHex: n });
  }

  return (
    <Card className="p-6">
      <SectionLabel>Couleur d'accent</SectionLabel>
      <p className="mt-1.5 text-[13px] text-ink-600">
        Utilisée pour les titres-clés, badges et liens dans vos fiches.
      </p>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {ACCENT_PRESETS.map((p) => {
          const active =
            palette.accentHex.toUpperCase() === p.hex.toUpperCase();
          return (
            <button
              key={p.id}
              onClick={() => onChange({ accentHex: p.hex })}
              title={`${p.label} — ${p.hex}`}
              className={`w-8 h-8 rounded-full hairline transition ${
                active ? "ring-2 ring-ink-900 ring-offset-2 ring-offset-paper" : ""
              }`}
              style={{ background: p.hex }}
            />
          );
        })}
        <div className="h-6 w-px bg-ink-200 mx-1" />
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={palette.accentHex}
            onChange={(e) => onChange({ accentHex: e.target.value.toUpperCase() })}
            className="w-8 h-8 rounded-full hairline cursor-pointer p-0"
            aria-label="Sélecteur couleur"
          />
          <div className="relative">
            <input
              type="text"
              value={hexInput}
              onChange={(e) => setHexInput(e.target.value)}
              onBlur={(e) => commitHex(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitHex(hexInput);
              }}
              className="w-[96px] h-8 rounded-md hairline px-2 font-mono text-[12px] uppercase bg-paper outline-none focus:ring-2 focus:ring-lime-300"
              placeholder="#D9EA2C"
            />
          </div>
        </div>
      </div>

      {contrastWarning && (
        <div className="mt-3 text-[12px] text-brick flex items-center gap-1.5">
          <Ic.Bolt size={12} /> {contrastWarning}
        </div>
      )}
    </Card>
  );
}

function TypographySection({
  typography,
  onChange,
}: {
  typography: Typography;
  onChange: (patch: Partial<Typography>) => void;
}) {
  return (
    <Card className="p-6">
      <SectionLabel>Typographie</SectionLabel>
      <p className="mt-1.5 text-[13px] text-ink-600">
        Choisissez un pairing. Toutes les fiches utilisent ces deux familles.
      </p>
      <div className="mt-4 grid gap-2 sm:grid-cols-3">
        {(Object.keys(TYPOGRAPHY_PAIRINGS) as TypographyPairing[]).map((k) => {
          const p = TYPOGRAPHY_PAIRINGS[k];
          const active = typography.pairing === k;
          return (
            <button
              key={k}
              onClick={() => onChange({ pairing: k })}
              className={`text-left p-4 rounded-xl2 hairline transition ${
                active ? "bg-ink-900 text-white" : "bg-paper hover:bg-ink-50"
              }`}
            >
              <div className="text-[11px] uppercase tracking-[0.14em] opacity-70">
                {p.label}
              </div>
              <div
                className="mt-2 text-[20px] leading-tight"
                style={{ fontFamily: `"${p.serif}", serif` }}
              >
                Potentiel
              </div>
              <div
                className="text-[12px] opacity-80"
                style={{ fontFamily: `"${p.sans}", sans-serif` }}
              >
                {p.sub}
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

function AgentSection({
  agent,
  userEmail,
  onChange,
}: {
  agent: AgentSignature;
  userEmail: string | null;
  onChange: (patch: Partial<AgentSignature>) => void;
}) {
  const [uploading, setUploading] = useState(false);

  async function onPickPhoto(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const upload = await uploadImage("agent-photo", file);
      const prev = agent.photoPath;
      onChange({ photoUrl: upload.url, photoPath: upload.path });
      if (prev && prev !== upload.path) deleteImage(prev);
    } catch (e) {
      console.error("[agent] upload failed", e);
      alert("Upload de la photo impossible. Réessayez.");
    } finally {
      setUploading(false);
    }
  }

  async function removePhoto() {
    const prev = agent.photoPath;
    onChange({ photoUrl: null, photoPath: null });
    if (prev) deleteImage(prev);
  }

  return (
    <Card className="p-6">
      <SectionLabel>Signature agent</SectionLabel>
      <p className="mt-1.5 text-[13px] text-ink-600">
        Affichée en pied de chaque fiche — humanise le document.
      </p>

      <div className="mt-4 flex items-start gap-4">
        <div className="w-[80px] h-[80px] rounded-full bg-ink-50 hairline overflow-hidden grid place-items-center shrink-0">
          {agent.photoUrl ? (
            <img
              src={agent.photoUrl}
              alt={agent.name ?? ""}
              className="w-full h-full object-cover"
            />
          ) : (
            <Ic.User size={22} className="text-ink-400" />
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap pt-2">
          <label className="cursor-pointer">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onPickPhoto}
              disabled={uploading}
            />
            <span className="inline-flex items-center justify-center gap-2 rounded-full font-medium transition h-8 px-3 text-[12.5px] bg-paper text-ink-900 hairline hover:bg-ink-50">
              <Ic.Plus size={13} />
              <span>{agent.photoUrl ? "Remplacer" : "Ajouter"}</span>
            </span>
          </label>
          {agent.photoUrl && (
            <Button variant="ghost" size="sm" onClick={removePhoto}>
              Supprimer
            </Button>
          )}
          {uploading && <span className="text-[12px] text-ink-500">Envoi…</span>}
        </div>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <Field
          label="Nom complet"
          value={agent.name ?? ""}
          onChange={(v) => onChange({ name: v || null })}
          placeholder="Camille Perrin"
        />
        <Field
          label="N° RSAC / carte pro"
          value={agent.rsac ?? ""}
          onChange={(v) => onChange({ rsac: v || null })}
          placeholder="RSAC 123 456 789"
        />
        <Field
          label="Téléphone direct"
          value={agent.phone ?? ""}
          onChange={(v) => onChange({ phone: v || null })}
          placeholder="+33 6 12 34 56 78"
        />
        <Field
          label="Email"
          value={agent.email ?? ""}
          onChange={(v) => onChange({ email: v || null })}
          placeholder={userEmail ?? "camille@hexagone.fr"}
        />
        <div className="sm:col-span-2">
          <Field
            label="Phrase de signature"
            value={agent.tagline ?? ""}
            onChange={(v) => onChange({ tagline: v || null })}
            placeholder="Votre conseiller sur le 92"
            hint="Une ligne perso qui apparaît sous votre nom."
          />
        </div>
      </div>
    </Card>
  );
}

function LegalSection({
  agency,
  onChange,
}: {
  agency: Agency;
  onChange: (patch: Partial<Agency>) => void;
}) {
  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <SectionLabel>Mentions légales (Hoguet)</SectionLabel>
          <p className="mt-1.5 text-[13px] text-ink-600 max-w-[460px]">
            Requis pour publier une fiche. Affichées en pied de page dans tous
            les exports.
          </p>
        </div>
        <Pill tone="soft" icon={<Ic.Shield size={12} />}>
          Conformité
        </Pill>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field
            label="Adresse de l'agence"
            value={agency.address ?? ""}
            onChange={(v) => onChange({ address: v || null })}
            placeholder="42 rue du Commerce, 92100 Boulogne"
          />
        </div>
        <Field
          label="SIRET"
          value={agency.siret ?? ""}
          onChange={(v) => onChange({ siret: v || null })}
          placeholder="123 456 789 00012"
        />
        <Field
          label="Carte T"
          value={agency.carteT ?? ""}
          onChange={(v) => onChange({ carteT: v || null })}
          placeholder="CPI 7501 2024 000 012 345"
        />
        <Field
          label="RCS"
          value={agency.rcs ?? ""}
          onChange={(v) => onChange({ rcs: v || null })}
          placeholder="RCS Nanterre 123 456 789"
        />
        <Field
          label="RC Pro"
          value={agency.rcPro ?? ""}
          onChange={(v) => onChange({ rcPro: v || null })}
          placeholder="MMA — Police n°123456789"
        />
        <div className="sm:col-span-2">
          <Field
            label="Garantie financière"
            value={agency.garantieFinanciere ?? ""}
            onChange={(v) => onChange({ garantieFinanciere: v || null })}
            placeholder="Galian — 120 000 €"
            hint="Requis uniquement si vous manipulez des fonds clients."
          />
        </div>
      </div>
    </Card>
  );
}

/* ═══════════════ Primitives ═══════════════ */

function Field({
  label,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="text-[12px] font-medium text-ink-700">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full h-10 rounded-lg hairline px-3 bg-paper text-[13.5px] outline-none focus:ring-2 focus:ring-lime-300"
      />
      {hint && <span className="mt-1 block text-[11.5px] text-ink-500">{hint}</span>}
    </label>
  );
}

function SaveIndicator({
  status,
  readiness,
}: {
  status: SaveStatus;
  readiness: ReturnType<typeof brandingReadiness>;
}) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2">
        {readiness.ready ? (
          <Pill tone="green" icon={<Ic.Check size={12} />}>
            Prêt à publier
          </Pill>
        ) : (
          <Pill tone="amber" icon={<Ic.Bolt size={12} />}>
            {readiness.missing.length} champ
            {readiness.missing.length > 1 ? "s" : ""} requis
          </Pill>
        )}
        {!readiness.ready && (
          <span className="text-[11.5px] text-ink-500 truncate">
            {readiness.missing.slice(0, 3).join(" · ")}
            {readiness.missing.length > 3 ? "…" : ""}
          </span>
        )}
      </div>
      <SaveDot status={status} />
    </div>
  );
}

function SaveDot({ status }: { status: SaveStatus }) {
  const map: Record<SaveStatus, { label: string; color: string }> = {
    idle: { label: "Sauvegardé", color: "bg-ink-300" },
    saving: { label: "Enregistrement…", color: "bg-amber-400" },
    saved: { label: "Enregistré", color: "bg-emerald-500" },
    error: { label: "Erreur — réessayer", color: "bg-rose-500" },
  };
  const m = map[status];
  return (
    <div className="inline-flex items-center gap-1.5 text-[11.5px] text-ink-500">
      <span className={`w-1.5 h-1.5 rounded-full ${m.color}`} />
      {m.label}
    </div>
  );
}

/* ═══════════════ Preview ═══════════════ */

function PreviewPanel({ branding }: { branding: Branding }) {
  const { palette, typography, agency, agent } = branding;
  const pairing = TYPOGRAPHY_PAIRINGS[typography.pairing];
  const accent = palette.accentHex;
  const accentInk = readableForeground(accent);
  const fontSans = `"${pairing.sans}", ui-sans-serif, system-ui, sans-serif`;
  const fontSerif = `"${pairing.serif}", ui-serif, Georgia, serif`;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <SectionLabel>Aperçu fiche</SectionLabel>
        <span className="text-[11px] text-ink-500">Mis à jour en direct</span>
      </div>

      <div
        className="rounded-xl2 hairline overflow-hidden shadow-card bg-paper"
        style={{ fontFamily: fontSans }}
      >
        {/* Cover */}
        <div className="p-5 border-b border-ink-200/70">
          <div className="flex items-center justify-between gap-3 min-h-[32px]">
            <div className="flex items-center gap-2 min-w-0">
              {agency.logoUrl ? (
                <img
                  src={agency.logoUrl}
                  alt=""
                  className="h-6 max-w-[100px] object-contain"
                />
              ) : (
                <div
                  className="h-6 w-6 rounded"
                  style={{ background: accent }}
                />
              )}
              <div className="min-w-0">
                <div className="text-[11px] font-semibold truncate">
                  {agency.name || "Nom de l'agence"}
                </div>
                {agency.baseline && (
                  <div className="text-[9.5px] text-ink-500 truncate">
                    {agency.baseline}
                  </div>
                )}
              </div>
            </div>
            <span
              className="text-[9.5px] font-medium px-2 py-0.5 rounded-full"
              style={{ background: accent, color: accentInk }}
            >
              Fiche potentiel
            </span>
          </div>

          <div className="mt-5">
            <div className="text-[9.5px] uppercase tracking-[0.14em] text-ink-500">
              Prise de mandat · Zone UA
            </div>
            <h3
              className="mt-1.5 text-[22px] leading-[1.1] tracking-tight text-ink-900"
              style={{ fontFamily: fontSerif }}
            >
              12 rue des Lilas,
              <br />
              <span className="text-ink-500 italic">Boulogne-Billancourt</span>
            </h3>
          </div>

          <div className="mt-4 flex items-baseline gap-2">
            <div
              className="text-[30px] font-semibold tracking-tight tabular"
              style={{ color: accent === "#FFFFFF" ? "#14161A" : accent, fontFamily: fontSerif }}
            >
              +47 m²
            </div>
            <div className="text-[11px] text-ink-600">
              d'extension autorisés
            </div>
          </div>
          <div className="mt-1 text-[10.5px] text-ink-500">
            ≈ +280 k€ de valeur potentielle
          </div>
        </div>

        {/* Key data */}
        <div className="grid grid-cols-3 border-b border-ink-200/70">
          {[
            { l: "Emprise", v: "60 %" },
            { l: "Hauteur", v: "12 m" },
            { l: "CES résiduel", v: "32 m²" },
          ].map((k) => (
            <div
              key={k.l}
              className="p-3 border-r last:border-r-0 border-ink-200/70"
            >
              <div className="text-[9.5px] text-ink-500 uppercase tracking-wider">
                {k.l}
              </div>
              <div
                className="mt-0.5 text-[15px] font-semibold tabular"
                style={{ fontFamily: fontSerif }}
              >
                {k.v}
              </div>
            </div>
          ))}
        </div>

        {/* Agent signature */}
        <div className="p-4 flex items-center gap-3 border-b border-ink-200/70">
          <div
            className="w-10 h-10 rounded-full overflow-hidden grid place-items-center shrink-0"
            style={{ background: accent }}
          >
            {agent.photoUrl ? (
              <img
                src={agent.photoUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <Ic.User size={16} className="text-ink-900 opacity-60" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[12px] font-semibold truncate">
              {agent.name || "Nom de l'agent"}
            </div>
            {agent.tagline && (
              <div className="text-[10.5px] text-ink-600 italic truncate">
                {agent.tagline}
              </div>
            )}
            <div className="text-[10px] text-ink-500 truncate">
              {agent.phone || "—"}
              {agent.email && (
                <>
                  <span className="mx-1">·</span>
                  {agent.email}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Legal footer */}
        <div className="p-3 bg-ink-50 text-[8.5px] text-ink-500 leading-relaxed">
          <span className="font-semibold text-ink-700">
            {agency.name || "Agence"}
          </span>
          {agency.address && <> · {agency.address}</>}
          {agency.siret && <> · SIRET {agency.siret}</>}
          {agency.carteT && (
            <>
              <br />
              {agency.carteT}
            </>
          )}
          {agency.rcs && <> · {agency.rcs}</>}
          {agency.rcPro && <> · {agency.rcPro}</>}
          {agency.garantieFinanciere && <> · {agency.garantieFinanciere}</>}
        </div>
      </div>

      <div className="text-[11px] text-ink-500 leading-relaxed">
        Aperçu indicatif. Le PDF final est généré côté serveur avec un rendu
        typographique de qualité — à venir.
      </div>
    </div>
  );
}

/* ═══════════════ Default export (allows lazy/named either way) ═══════════════ */

export default BrandingTab;

// Re-exports so callers don't need to import from `../lib/branding` directly.
export type { Branding } from "../lib/branding";
